<script setup lang="ts">
import { api } from '@/api';
import type {
  CreateOtherQaEquipmentDto,
  CreateQaEquipmentRtSystemDto,
  QaEquipmentRtSystemDto,
  ZeroMeasurementParametersDto,
} from '@/api/generator';
import VeeDurationField from '@/components/forms/DurationField/VeeDurationField.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { formBackendErrorHandler } from '@/errorHandling';
import { useLoading } from '@/utils/useLoading';
import { zeroMeasurementParametersSaveKey } from '@/views/qaEquipment/types.ts';
import { computed, inject, onBeforeUnmount, onMounted, watch } from 'vue';

type QaEquipmentFormConfigurationTabProps = {
  qaEquipment: Partial<
    QaEquipmentRtSystemDto | CreateQaEquipmentRtSystemDto | CreateOtherQaEquipmentDto
  >;
};
const props = defineProps<QaEquipmentFormConfigurationTabProps>();

// Mode of the surrounding equipment form. Read before the tab's own form is
// created, because that one provides a mode of its own to the fields below.
const parentMode = useFormMode();

const equipmentId = computed(() => ('id' in props.qaEquipment ? props.qaEquipment.id : undefined));

// The zero measurement parameters are a resource of their own with a separate
// endpoint, so they get their own form instead of extra fields on the equipment
// DTO (whose schema forbids additional properties). The parent form owns the
// unsaved-changes guard.
const { form } = useVeeForm<ZeroMeasurementParametersDto>({
  schemaName: 'ZeroMeasurementParametersDto',
  mode: () => parentMode.value,
  confirmationOptions: { skipDirtyCheck: 'always' },
});
const { meta, resetForm, validate, values } = form;

const { action: loadParameters } = useLoading(async () => {
  if (!equipmentId.value) {
    return;
  }
  const { result } = await api.qaEquipmentApi.getZeroMeasurementParameters({
    id: equipmentId.value,
  });
  if (result) {
    resetForm({ values: result });
  }
});

onMounted(loadParameters);
watch(equipmentId, loadParameters);

// Called by the edit view after the entity itself was saved.
async function save(): Promise<boolean> {
  if (!equipmentId.value || !meta.value.dirty) {
    return true;
  }
  const { valid } = await validate();
  if (!valid) {
    return false;
  }
  const { success, result } = await api.qaEquipmentApi.saveZeroMeasurementParameters(
    { id: equipmentId.value, zeroMeasurementParametersDto: { ...values } },
    [formBackendErrorHandler(form)],
  );
  if (success && result) {
    resetForm({ values: result });
  }
  return success;
}

// Only the edit view provides the hook; the details view just reads.
const registerSave = inject(zeroMeasurementParametersSaveKey, undefined);
let unregisterSave: (() => void) | undefined;
onMounted(() => {
  unregisterSave = registerSave?.(save);
});
onBeforeUnmount(() => {
  unregisterSave?.();
});
</script>

<template>
  <h4 class="mb-4">{{ $t('ZeroMeasurement') }}</h4>
  <v-row>
    <v-col cols="12" md="3">
      <VeeNumberField
        name="zeroMeasurementDuration"
        :label="$t('ZeroMeasurementDuration')"
        suffix="s"
      />
    </v-col>
    <v-col cols="12" md="6">
      <VeeDurationField
        name="zeroMeasurementValidityDuration"
        :label="$t('ZeroMeasurementValidityDuration')"
      />
    </v-col>
  </v-row>
</template>
